/*
  Pawmerly behaviour.
  1. Shrine's VariantRadios never updates the option legend's [data-selected-value]; keep it in step with the checked input.
  2. Band headings fade in once when scrolled into view (only when the Motion setting is on and reduced motion is not requested).
  3. The sticky Add to cart bar submits the main product form, so it must never be enabled while the main button is not:
     mirror the main button's disabled state and label (e.g. "Unavailable" for a combination that does not exist).
  4. Choose a size: Shrine pre-selects the first size. On a fresh product page (no ?variant=) of a product with more than one
     size, no size shows as chosen until the shopper picks one; Add to cart (main or sticky) then scrolls to the sizes
     with a message instead of adding the default size, and the express buttons stay hidden.
  5. <pm-rail>: prev/next buttons and a "1–4 / 10" counter for a horizontal scroll-snap track (the track itself scrolls natively).
  6. Recently viewed: the product page records its handle in localStorage; [data-pm-recent] sections fetch those products'
     current data (/products/<handle>.js) and render small cards. Storage or network failures leave the section hidden.
  7. Details tabs (role=tablist): click selects; arrow keys, Home and End move between tabs.
*/

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const fieldset = input.closest('variant-radios fieldset, variant-selects fieldset');
  const label = fieldset && fieldset.querySelector('[data-selected-value]');
  if (label) label.textContent = input.value === '-' ? (window.pmStrings && window.pmStrings.optionNone) || input.value : input.value;
});

(() => {
  const root = document.documentElement;
  const reveal = () => {
    const items = document.querySelectorAll('.pm-reveal:not(.is-in)');
    if (!items.length) return;
    if (!root.hasAttribute('data-pm-motion') || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    root.classList.add('pm-motion-ready');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((el) => io.observe(el));
  };
  reveal();
  document.addEventListener('shopify:section:load', reveal);
})();

(() => {
  const mirror = (sticky) => {
    const formId = sticky.getAttribute('form');
    const form = formId && document.getElementById(formId);
    const main = form && form.querySelector('button[name="add"]');
    if (!main || main === sticky) return;
    const sync = () => {
      sticky.disabled = main.disabled;
      sticky.toggleAttribute('aria-disabled', main.disabled);
      const from = main.querySelector('span');
      const to = sticky.querySelector('span');
      if (main.disabled && from && to) to.textContent = from.textContent;
      else if (to && sticky.dataset.label) to.textContent = sticky.dataset.label;
    };
    new MutationObserver(sync).observe(main, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    sync();
  };
  const init = () => document.querySelectorAll('.sticky-atc button[name="add"]').forEach(mirror);
  init();
  document.addEventListener('shopify:section:load', init);
})();

(() => {
  const strings = window.pmStrings || {};
  const sizeFieldset = (info) =>
    Array.from(info.querySelectorAll('variant-radios fieldset')).find((fs) => {
      const legend = fs.querySelector('legend');
      return legend && /^\s*size/i.test(legend.firstChild ? legend.firstChild.textContent : '') && fs.querySelectorAll('input[type="radio"]').length > 1;
    });
  const init = () => {
    if (new URLSearchParams(window.location.search).has('variant')) return;
    document.querySelectorAll('product-info.product__info-container:not([data-pm-size-ready])').forEach((info) => {
      info.setAttribute('data-pm-size-ready', '');
      const fs = sizeFieldset(info);
      if (!fs) return;
      fs.setAttribute('data-pm-size', '');
      info.setAttribute('data-pm-needs-size', '');
      const value = fs.querySelector('[data-selected-value]');
      if (value) value.textContent = strings.chooseSize || '';
      // Really unchecked, so assistive tech and the keyboard agree with what is shown; Shrine waits (see the change guard).
      fs.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
      const msg = document.createElement('p');
      msg.className = 'pm-size-msg';
      msg.id = `PmSizeMsg-${info.id || Math.random().toString(36).slice(2)}`;
      msg.setAttribute('role', 'alert');
      msg.hidden = true;
      fs.after(msg);
      fs.setAttribute('aria-describedby', msg.id);
      const from = document.createElement('span');
      from.className = 'pm-from';
      from.textContent = `${strings.from || 'From'} `;
      const price = info.querySelector('.price .price__regular .price-item--regular, .price .price-item--last');
      if (price) price.prepend(from);
      const chosen = () => {
        if (!info.hasAttribute('data-pm-needs-size')) return;
        info.removeAttribute('data-pm-needs-size');
        msg.hidden = true;
        msg.textContent = '';
        from.remove();
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (value && checked) value.textContent = checked.value;
      };
      fs.addEventListener('change', chosen);
    });
  };
  // While no size is chosen, a change to another option (style, colour) only updates its legend: Shrine would otherwise
  // look up a variant with no size and show "Unavailable". Picking the size lets Shrine resolve the full variant.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const info = input.closest('product-info[data-pm-needs-size]');
      if (!info || input.closest('fieldset[data-pm-size]')) return;
      event.stopImmediatePropagation();
      const label = input.closest('fieldset') && input.closest('fieldset').querySelector('[data-selected-value]');
      if (label) label.textContent = input.value;
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id) return;
      const info = Array.from(document.querySelectorAll('product-info[data-pm-needs-size]')).find((el) => el.contains(form) || el.querySelector(`[form="${form.id}"]`) || form.id.endsWith(el.dataset.section || '\u0000'));
      if (!info) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const fs = info.querySelector('fieldset[data-pm-size]');
      const msg = fs && fs.nextElementSibling;
      if (msg && msg.classList.contains('pm-size-msg')) {
        msg.hidden = false;
        msg.textContent = strings.sizeError || '';
      }
      if (fs) {
        fs.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        const first = fs.querySelector('input[type="radio"]:not(.disabled)') || fs.querySelector('input[type="radio"]');
        if (first) first.focus({ preventScroll: true });
      }
    },
    true
  );
  init();
  document.addEventListener('shopify:section:load', init);
})();

(() => {
  if (customElements.get('pm-rail')) return;
  class PmRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pm-rail__track');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.count = this.querySelector('[data-rail-count]');
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', () => window.requestAnimationFrame(this.update), { passive: true });
      window.addEventListener('resize', this.update, { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.update();
    }
    step() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth || 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap || 1;
    }
    perView() {
      return Math.max(1, Math.floor((this.track.clientWidth + 4) / this.step()));
    }
    go(dir) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: dir * this.step() * this.perView(), behavior: reduce ? 'auto' : 'smooth' });
    }
    update() {
      const total = this.track.children.length;
      const view = this.perView();
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= max;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd || max <= 0;
      if (!this.count) return;
      if (total <= view) {
        this.count.textContent = '';
        return;
      }
      const first = atEnd ? total - view + 1 : Math.min(total, Math.round(this.track.scrollLeft / this.step()) + 1);
      const last = Math.min(total, first + view - 1);
      this.count.textContent = first === last ? `${first} / ${total}` : `${first}–${last} / ${total}`;
    }
  }
  customElements.define('pm-rail', PmRail);
})();

(() => {
  const strings = window.pmStrings || {};
  const key = strings.recentKey || 'pm-recent';
  const read = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };
  const record = (handle) => {
    try {
      const list = [handle, ...read().filter((h) => h !== handle)].slice(0, 16);
      window.localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable: nothing to remember */
    }
  };
  const money = (cents) => {
    const format = strings.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount\w*\s*\}\}/, amount);
  };
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  };
  const card = (p) => {
    const li = document.createElement('li');
    const a = el('a', 'pm-mini');
    a.href = p.url;
    const media = el('span', 'pm-mini__media');
    if (p.featured_image) {
      const img = document.createElement('img');
      const src = p.featured_image.startsWith('//') ? `https:${p.featured_image}` : p.featured_image;
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=500`;
      img.alt = '';
      img.width = 500;
      img.height = 500;
      img.loading = 'lazy';
      media.append(img);
    }
    const priceText = p.price_varies && strings.fromPrice ? strings.fromPrice.replace('[price]', money(p.price_min)) : money(p.price_min);
    a.append(media, el('span', 'pm-mini__vendor', p.vendor), el('span', 'pm-mini__title', p.title), el('span', 'pm-mini__price', priceText));
    li.append(a);
    return li;
  };
  const render = async (section) => {
    if (section.dataset.done) return;
    section.dataset.done = '1';
    const exclude = section.dataset.exclude;
    const limit = parseInt(section.dataset.limit, 10) || 8;
    const handles = read().filter((h) => h !== exclude).slice(0, limit);
    if (!handles.length) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`${root}products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    const products = results.filter((p) => p && p.url);
    if (!products.length) return;
    const list = section.querySelector('[data-recent-list]');
    products.forEach((p) => list.append(card(p)));
    section.hidden = false;
    const rail = section.querySelector('pm-rail');
    if (rail && rail.update) rail.update();
  };
  const init = () => {
    const meta = document.querySelector('[data-pm-product-handle]');
    if (meta) record(meta.dataset.pmProductHandle);
    document.querySelectorAll('[data-pm-recent]').forEach(render);
  };
  init();
  document.addEventListener('shopify:section:load', init);
})();

document.addEventListener('keydown', (event) => {
  const tab = event.target.closest && event.target.closest('[role="tab"]');
  if (!tab) return;
  const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
  const i = tabs.indexOf(tab);
  const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[event.key];
  if (next === undefined) return;
  event.preventDefault();
  const target = tabs[(next + tabs.length) % tabs.length];
  target.focus();
  target.click();
});
document.addEventListener('click', (event) => {
  const tab = event.target.closest && event.target.closest('[role="tab"]');
  if (!tab) return;
  tab
    .closest('[role="tablist"]')
    .querySelectorAll('[role="tab"]')
    .forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
});

// 8. Shrine's VariantSelects rewrites every <option> label with its raw value on each change; keep "-" reading as "None".
(() => {
  const VS = customElements.get('variant-selects');
  if (!VS || VS.prototype.pmNonePatched) return;
  const original = VS.prototype.setInputAvailability;
  VS.prototype.pmNonePatched = true;
  VS.prototype.setInputAvailability = function (list, available) {
    original.call(this, list, available);
    const none = (window.pmStrings && window.pmStrings.optionNone) || 'None';
    list.forEach((el) => {
      if (el.tagName === 'OPTION' && el.getAttribute('value') === '-') el.textContent = el.textContent.replace(/^-/, none);
    });
  };
})();

// 9. Collection chips: scroll the current category / brand chip into view inside its horizontal row.
(() => {
  const show = () =>
    document.querySelectorAll('.pm-jobs__list').forEach((list) => {
      const current = list.querySelector('[aria-current="page"]:not(.pm-chip--small)') || null;
      if (current && list.scrollWidth > list.clientWidth) list.scrollLeft = current.closest('li').offsetLeft - list.offsetLeft - 16;
    });
  show();
  document.addEventListener('shopify:section:load', show);
})();

// 10. Size drawing follows the chosen size: a Size radio change selects the size-guide tab whose name starts the value
//     ("Giant – 152 × 122 × 18 cm" → "Giant").
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.closest('fieldset[data-pm-size], fieldset')) return;
  const legend = input.closest('fieldset').querySelector('legend');
  if (!legend || !/^\s*size/i.test(legend.firstChild ? legend.firstChild.textContent : '')) return;
  const name = input.value.split(' – ')[0].trim().toLowerCase();
  document.querySelectorAll('.pm-fitting__tab').forEach((tab) => {
    if (tab.textContent.trim().toLowerCase() === name && tab.getAttribute('aria-selected') !== 'true') tab.click();
  });
});
